import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { NavigateFunction } from 'react-router-dom';
import { PaginationControl } from 'shared/components/pagination';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import {
   Resume,
   ResumeActionResponse,
   ResumeIndexRequestDto,
   ResumeIndexResponse,
} from './types';

type ResumeActionHandlers = {
   onSuccess?: (resp: ResumeActionResponse) => void;
   onError?: (message: string) => void;
};

export class ResumeManagerStore {
   readonly loadResumesState = new AsyncState<ResumeIndexResponse>();
   readonly activateResumeState = new AsyncState<ResumeActionResponse>();
   readonly deactivateResumeState = new AsyncState<ResumeActionResponse>();
   readonly deleteResumeState = new AsyncState<ResumeActionResponse>();
   readonly verifyResumeState = new AsyncState<ResumeActionResponse>();

   rawResumes: Resume[] = [];
   idsArray: number[] = [];
   searchStr = '';
   totalCount = 0;
   positionFrom = 0;
   toPage = '1';

   private readonly PER_PAGE = 50;
   readonly pagination = new PaginationControl();

   constructor(private readonly navigate: NavigateFunction) {
      makeObservable(this, {
         rawResumes: observable,
         idsArray: observable,
         searchStr: observable,
         totalCount: observable,
         positionFrom: observable,
         toPage: observable,
         resumes: computed,
         totalPages: computed,
         loading: computed,
         selectedAll: computed,
         disableBulkActions: computed,
         recordStart: computed,
         recordEnd: computed,
         onSearchChange: action.bound,
         onSelectAll: action.bound,
         onDeselectAll: action.bound,
         onToggleById: action.bound,
         onToPageChange: action.bound,
      });
   }

   get resumes(): Resume[] {
      return this.rawResumes;
   }

   get totalPages(): number {
      return Math.ceil(this.totalCount / this.PER_PAGE);
   }

   get loading(): boolean {
      return this.loadResumesState.status === 'pending';
   }

   get selectedAll(): boolean {
      return this.rawResumes.length !== 0 && this.idsArray.length === this.rawResumes.length;
   }

   get disableBulkActions(): boolean {
      return true;
   }

   get recordStart(): number {
      return this.totalCount === 0 ? 0 : this.positionFrom + 1;
   }

   get recordEnd(): number {
      return Math.min(this.positionFrom + this.rawResumes.length, this.totalCount);
   }

   onSearchChange(value: string): void {
      this.searchStr = value.toLowerCase();
   }

   onSelectAll(): void {
      this.idsArray = this.rawResumes.map((resume) => resume.id);
   }

   onDeselectAll(): void {
      this.idsArray = [];
   }

   onToggleById(id: number): void {
      if (this.idsArray.includes(id)) {
         this.idsArray = this.idsArray.filter((resumeId) => resumeId !== id);
      } else {
         this.idsArray = [...this.idsArray, id];
      }
   }

   onToPageChange(value: string): void {
      if (/^\d+$/.test(value) || value === '') {
         this.toPage = value;
      }
   }

   async submitSearch(): Promise<void> {
      this.pagination.setPage(0);
      this.toPage = '1';
      await this.loadResumes(0);
   }

   async loadPage(page: number): Promise<void> {
      this.pagination.setPage(page);
      this.toPage = String(page + 1);
      await this.loadResumes(page * this.PER_PAGE);
   }

   async goToPage(): Promise<void> {
      if (this.totalPages === 0) {
         return;
      }

      if (this.toPage === '') {
         this.toPage = '1';
      }

      const page = Number(this.toPage);
      if (Number.isNaN(page)) {
         return;
      }

      const nextPage = Math.min(Math.max(page, 1), this.totalPages) - 1;
      await this.loadPage(nextPage);
   }

   async loadResumes(positionFrom = this.positionFrom): Promise<void> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      const payload: ResumeIndexRequestDto = {
         searchQuery: this.searchStr,
         positionFrom,
         limit: this.PER_PAGE,
      };

      runInAction(() => {
         this.idsArray = [];
      });

      await this.loadResumesState.run(
         () =>
            HttpClient.post<ResumeIndexResponse>('/admin/resumes/index', payload, {
               headers: {
                  adminid: adminID,
                  Authorization: `Bearer ${tokenKey}`,
               },
            }),
         {
            onSuccess: (resp) => {
               if (resp.status !== 200) {
                  return;
               }

               const nextPosition = Number(resp.response.positionFrom) || 0;
               const nextPage = Math.floor(nextPosition / this.PER_PAGE);
               const responseList = resp.response.cv_list ?? [];

               runInAction(() => {
                  this.rawResumes = responseList;
                  this.totalCount = Number(resp.response.totalCount) || 0;
                  this.positionFrom = nextPosition;
                  this.toPage = String(nextPage + 1);
               });

               this.pagination.setPage(nextPage);
            },
         }
      );
   }

   async activateResume({ id, onSuccess, onError }: ResumeActionHandlers & { id: number }) {
      await this.runResumeAction({
         state: this.activateResumeState,
         request: () => this.postAdminResumeAction(`/admin/resumes/activate/${id}`),
         onSuccess,
         onError,
      });
   }

   async deactivateResume({ id, onSuccess, onError }: ResumeActionHandlers & { id: number }) {
      await this.runResumeAction({
         state: this.deactivateResumeState,
         request: () => this.postAdminResumeAction(`/admin/resumes/deactivate/${id}`),
         onSuccess,
         onError,
      });
   }

   async verifyResume({ id, onSuccess, onError }: ResumeActionHandlers & { id: number }) {
      await this.runResumeAction({
         state: this.verifyResumeState,
         request: () => this.postAdminResumeAction(`/admin/resumes/verify/${id}`),
         onSuccess,
         onError,
      });
   }

   async deleteResume({ id, onSuccess, onError }: ResumeActionHandlers & { id: number }) {
      await this.runResumeAction({
         state: this.deleteResumeState,
         request: () => {
            const tokenKey = Cookies.get('token');
            const adminID = Cookies.get('adminID');

            return HttpClient.post<ResumeActionResponse>(
               '/admin/resumes/cvDelete',
               { id },
               {
                  headers: {
                     adminid: adminID,
                     Authorization: `Bearer ${tokenKey}`,
                  },
               }
            );
         },
         onSuccess,
         onError,
      });
   }

   private postAdminResumeAction(url: string): Promise<ResumeActionResponse> {
      const tokenKey = Cookies.get('token');
      const adminID = Cookies.get('adminID');

      return HttpClient.post<ResumeActionResponse>(url, null, {
         headers: {
            adminid: adminID,
            Authorization: `Bearer ${tokenKey}`,
         },
      });
   }

   private async runResumeAction({
      state,
      request,
      onSuccess,
      onError,
   }: ResumeActionHandlers & {
      state: AsyncState<ResumeActionResponse>;
      request: () => Promise<ResumeActionResponse>;
   }): Promise<void> {
      const tokenKey = Cookies.get('token');

      if (!tokenKey) {
         return this.navigate?.('/admin');
      }

      await state.run(request, {
         onSuccess: async (resp) => {
            if (resp.status === 200) {
               await this.loadResumes(this.positionFrom);
               onSuccess?.(resp);
            } else {
               onError?.(resp.message);
            }
         },
         onError: (error) => {
            onError?.(error.message);
         },
      });
   }
}
